import React, { useRef, useState, useCallback, memo, useEffect } from 'react';
import { Pills } from '@saleshandy/design-system';
import { accessibleOnClick } from '../../../../../../../shared/utils/accessible-on-click';

type IProps = {
  value: string[];
  onUpdateFilterConditionValue: (value: string[]) => void;
  disabled?: boolean;
  className?: string;
  validate?: (value: string[]) => boolean;
  maxChipsToShow?: number;
  customErrorMessage?: string;
  maxChip?: number;
  minChip?: number;
  maxEntryLength?: number;
  autoFocus?: boolean;
  hasError?: (error: boolean) => void;
};

const ChipTextInput: React.FC<IProps> = ({
  value = [],
  onUpdateFilterConditionValue = () => {},
  disabled = false,
  className = '',
  validate = () => true,
  maxChipsToShow = 3,
  customErrorMessage = 'Invalid entry',
  maxChip = 10,
  minChip = 3,
  maxEntryLength = 64,
  hasError,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const lastAddedRef = useRef<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isClickInsideRef = useRef(false);

  const [inputValue, setInputValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleFocus = useCallback(() => {
    setIsFocused(true);
    setIsExpanded(true);
  }, []);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
  }, []);

  useEffect(() => {
    if (value?.length === 0) {
      setErrorMessage(`You need to add at least ${minChip} items.`);
      hasError?.(true);
    }
  }, [value]);

  const addChip = () => {
    const entries = inputValue
      ?.split(',')
      ?.map((e) => e.trim())
      ?.filter(Boolean);

    const validEntries = entries?.filter((e) => e?.length <= maxEntryLength);

    if (validEntries?.length === 0 && entries?.length > 0) {
      setErrorMessage(`Each entry must be under ${maxEntryLength} characters.`);
      hasError?.(true);
      return;
    }

    // Prevent repeat of last auto-blur addition
    if (
      validEntries?.length === 1 &&
      inputValue?.trim() === lastAddedRef.current
    ) {
      setInputValue('');
      return;
    }

    const newValues = [...value, ...validEntries];

    if (newValues?.length > maxChip) {
      setErrorMessage(`You can add up to ${maxChip} items.`);
      hasError?.(true);
      setInputValue('');
      return;
    }
    if (validEntries?.length > 0) {
      if (validate(validEntries)) {
        onUpdateFilterConditionValue(newValues);
        lastAddedRef.current =
          validEntries?.length === 1 ? validEntries[0] : null;
        setInputValue('');
        const isValid = newValues?.length >= minChip;
        setErrorMessage(
          isValid ? '' : `You need to add at least ${minChip} items.`,
        );
        hasError?.(!isValid);
      } else {
        setErrorMessage(customErrorMessage);
        hasError?.(true);
      }
    } else {
      const isValid = value?.length >= minChip;
      setErrorMessage(
        isValid ? '' : `You need to add at least ${minChip} items.`,
      );
      hasError?.(!isValid);
    }
  };

  const handleMouseDown = useCallback((e: MouseEvent) => {
    if (containerRef.current?.contains(e.target as Node)) {
      isClickInsideRef.current = true;
    } else {
      isClickInsideRef.current = false;
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [handleMouseDown]);

  const onRemoveChip = (index: number) => {
    const updated = value?.filter((_, i) => i !== index);
    onUpdateFilterConditionValue(updated);
    hasError?.(updated.length < minChip);
    setErrorMessage(
      updated.length >= minChip
        ? ''
        : `You need to add at least ${minChip} items.`,
    );
    setIsExpanded(true);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      addChip();
    } else if (
      e.key === 'Backspace' &&
      inputValue.trim() === '' &&
      value.length > 0
    ) {
      onRemoveChip(value.length - 1);
    }
  };

  const onInputBlur = () => {
    setTimeout(() => {
      if (isClickInsideRef.current) {
        return;
      }

      setIsFocused(false);

      if (inputValue.trim()) {
        addChip();
      } else {
        const isValid = value?.length >= minChip;
        setErrorMessage(
          isValid ? '' : `You need to add at least ${minChip} items.`,
        );
        hasError?.(!isValid);
      }

      setIsExpanded(false);
    }, 100);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    lastAddedRef.current = null; // clear tracking on manual change
  };

  const handleContainerClick = () => {
    if (document.activeElement !== inputRef.current) {
      inputRef.current?.focus();
    }
  };

  const renderPills = () => {
    const pillsToShow =
      isExpanded || isFocused || value?.length <= maxChipsToShow
        ? value
        : value?.slice(0, maxChipsToShow);

    return (
      <>
        {pillsToShow?.map((text, index) => (
          <Pills
            // eslint-disable-next-line react/no-array-index-key
            key={`${text}-${index}`}
            theme="outline"
            size="md"
            label={text}
            showCloseIcon
            onClose={() => !disabled && onRemoveChip(index)}
          />
        ))}
        {!isExpanded && value?.length > maxChipsToShow && (
          <Pills
            key="more"
            theme="outline"
            size="md"
            label={`+${value.length - maxChipsToShow}`}
            onClick={() => setIsExpanded(true)}
          />
        )}
      </>
    );
  };

  return (
    <div className="chip-input-conatiner" ref={containerRef}>
      <div
        className={`chip-container ${className} ${isFocused ? 'focused' : ''} ${
          errorMessage ? 'error' : ''
        }`}
        {...accessibleOnClick(handleContainerClick)}
        onFocus={handleFocus}
        onBlur={handleBlur}
        tabIndex={-1}
      >
        <div className="prospect-filters-text-input-pills">
          {renderPills()}
          {isExpanded && (
            <input
              ref={inputRef}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={onKeyDown}
              onBlur={onInputBlur}
              placeholder="Enter text"
              disabled={disabled}
            />
          )}
        </div>
      </div>

      {errorMessage &&  (
        <div className="prospect-filters-text-input-error-message">
          <span>{errorMessage}</span>
        </div>
      )}
    </div>
  );
};

export default memo(ChipTextInput);
